import { useEffect, useState } from "react";
import { useViewScope } from "../auth/view-scope.js";

// 2.C.6 — Asset-thumbnail komponentti.
//
// <img src="/assets/:id/thumbnail?size=256" /> — selain seuraa 302-redirectin
// signed-URL:iin S3:hen automaattisesti. Jos 404 (no_thumbnail), 503
// (thumbnails_disabled), tai 202 (pending), nayttaa text-fallback:n.
//
// UM.6: <img> ei voi laehettaa custom-headeria, joten scope tulee
// query-paramina ?viewScope=... ja API:n middleware lukee siitä.
//
// UM.7 (video frames): videolle hover-tilassa cyclataan video-frame-N
// kehyksiae (N=0..4) ~500ms vaelein. ?frame=N URL-parametrina.
//
// onError-handleri triggeröityy kaikille ei-200-statuksille (selain ei
// kayta 404-vastauksen sisaltoa imageen). Pendings-tila (202) on yritetaan
// uudelleen pari kertaa N sekunnin valein, mutta yksinkertaisuuden vuoksi
// vaihe 2:ssa nayttaa heti placeholder:n.

const VIDEO_FRAME_COUNT = 5;
const FRAME_CYCLE_MS = 700;

export type AssetThumbnailProps = {
  assetId: string;
  /** Nayttoteksti placeholder-fallback:lle (esim. tiedostonimen patkasta). */
  fallbackLabel: string;
  /** Pyydetty koko. Default 256. */
  size?: 256 | 1024;
  /** Aria-alt. */
  alt?: string;
  className?: string;
  /** Jos true → hover-tilassa cyclata video-frame-N preview-kehyksiae. */
  isVideo?: boolean;
};

export function AssetThumbnail({
  assetId,
  fallbackLabel,
  size = 256,
  alt,
  className,
  isVideo = false,
}: AssetThumbnailProps) {
  const { headerValue } = useViewScope();
  const [error, setError] = useState(false);
  const [hover, setHover] = useState(false);
  const [frameIdx, setFrameIdx] = useState(0);

  // Hover-cycle: kun hover + video, vaihda kehys 700ms valein. Ensimmainen
  // vaihto (frame 1) tehdaan VAELITTOMASTI hover-aloituksen jaelkeen, jotta
  // kayttaja saa visuaalisen palautteen ettae cycle on kaynnissae. Sen jaelkeen
  // setInterval cyclaa 2 → 3 → 4 → 0 → 1 → ... loputtomiin.
  useEffect(() => {
    if (!hover || !isVideo) {
      setFrameIdx(0);
      return;
    }
    setFrameIdx(1);
    const t = setInterval(() => {
      setFrameIdx((i) => (i + 1) % VIDEO_FRAME_COUNT);
    }, FRAME_CYCLE_MS);
    return () => clearInterval(t);
  }, [hover, isVideo]);

  const baseClass =
    className ??
    "relative grid aspect-square place-items-center rounded bg-slate-800 text-lg font-medium text-slate-300 group-hover:bg-slate-700 overflow-hidden";

  if (error) {
    return <span className={baseClass}>{fallbackLabel}</span>;
  }

  const scopeParam =
    headerValue === "self" ? "" : `&viewScope=${encodeURIComponent(headerValue)}`;
  const frameParam = frameIdx > 0 ? `&frame=${frameIdx}` : "";

  // Pointer-events kattavat hiiren + trackpad + touch yhtenaisesti.
  // Synthetic React-eventit normalisoivat enter/leave-semantiikan jolloin
  // niitae ei tarvitse polyfillata kasin.
  const handlePointerEnter = () => {
    if (isVideo) setHover(true);
  };
  const handlePointerLeave = () => {
    if (isVideo) setHover(false);
  };

  return (
    <span
      className={baseClass}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onMouseEnter={handlePointerEnter}
      onMouseLeave={handlePointerLeave}
    >
      <img
        src={`/assets/${assetId}/thumbnail?size=${size}${frameParam}${scopeParam}`}
        alt={alt ?? ""}
        loading="lazy"
        onError={() => setError(true)}
        className="h-full w-full object-cover"
      />
      {isVideo && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white"
        >
          {hover ? `${frameIdx + 1}/${VIDEO_FRAME_COUNT}` : "VIDEO"}
        </span>
      )}
    </span>
  );
}
